/**
 * DoseTableView — editable dose table component.
 *
 * Displays dosimetry entries with inline editable fields for
 * InjectedActivity, TreatedTumors, and NonTumoralPerfusedLiver.
 * Changes trigger linear rescaling of all values.
 */

import { useState, useRef } from 'react';

import { DOSE_TABLE_INJECTED_ACTIVITY_KEY, IDoseTable, IDoseEntry } from '../domain/dose-table';
import { scaleDoseTableByActivity, scaleDoseTableByEntry } from '../domain/dose-table';

import { cn } from '@/lib/utils';

// ============================================================================
// Types
// ============================================================================

export interface DoseTableViewProps {
    /** Original dose table (at reference activity) */
    referenceTable: IDoseTable;
    /** Adjusted injected activity (or undefined for reference values) */
    adjustedActivityGBq?: number;
    /** Called when the user edits a value */
    onActivityChange: (newActivityGBq: number) => void;
    /** Whether the table is read-only */
    readOnly?: boolean;
}

// ============================================================================
// Helpers
// ============================================================================

function formatValue(value: number, unit: string): string {
    return unit === 'GBq' ? value.toFixed(2) : value.toFixed(4);
}

// ============================================================================
// Component
// ============================================================================

export function DoseTableView({
    referenceTable,
    adjustedActivityGBq,
    onActivityChange,
    readOnly = false,
}: DoseTableViewProps) {
    const displayTable =
        adjustedActivityGBq != null
            ? scaleDoseTableByActivity(referenceTable, adjustedActivityGBq)
            : referenceTable;

    const activityEntry = displayTable.entries.find((e) => e.key === DOSE_TABLE_INJECTED_ACTIVITY_KEY);
    if (!activityEntry) {
        return;
    }

    const doseEntries = displayTable.entries.filter((e) => e.key !== DOSE_TABLE_INJECTED_ACTIVITY_KEY);

    const handleEntryChange = (entry: IDoseEntry) => (newValue: number) => {
        if (entry.key === DOSE_TABLE_INJECTED_ACTIVITY_KEY) {
            onActivityChange(newValue);
        } else {
            const scaled = scaleDoseTableByEntry(referenceTable, entry.key, newValue);
            const act = scaled.entries.find((e) => e.key === DOSE_TABLE_INJECTED_ACTIVITY_KEY);
            if (act) onActivityChange(act.value);
        }
    };

    return (
        <div className="flex flex-col text-xs">
            {/* Activity — primary control, visually separated from anatomical doses */}
            <div className="grid grid-cols-[1fr_auto_auto] gap-x-2 pb-2 mb-2 border-b-2">
                <DoseRow
                    entry={activityEntry}
                    editable={!readOnly}
                    bold
                    onValueChange={handleEntryChange(activityEntry)}
                />
            </div>

            {/* Anatomical dose entries */}
            <div className="grid grid-cols-[1fr_auto_auto] gap-x-2 gap-y-0.5">
                {doseEntries.map((entry) => (
                    <DoseRow
                        key={entry.key}
                        entry={entry}
                        editable={!readOnly}
                        onValueChange={handleEntryChange(entry)}
                    />
                ))}
            </div>
        </div>
    );
}

// ============================================================================
// DoseRow
// ============================================================================

function DoseRow({
    entry,
    editable,
    bold = false,
    onValueChange,
}: {
    entry: IDoseEntry;
    editable: boolean;
    bold?: boolean;
    onValueChange: (newValue: number) => void;
}) {
    const [editing, setEditing] = useState(false);
    const [editValue, setEditValue] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);
    const committedRef = useRef(false);

    const startEdit = () => {
        if (!editable) return;
        committedRef.current = false;
        setEditValue(formatValue(entry.value, entry.unit));
        setEditing(true);
        setTimeout(() => inputRef.current?.select(), 0);
    };

    const commitEdit = () => {
        if (committedRef.current) return;
        committedRef.current = true;
        setEditing(false);
        const parsed = parseFloat(editValue);
        if (isNaN(parsed) || parsed < 0) return;
        if (Math.abs(parsed - entry.value) < 0.0001) return;
        onValueChange(parsed);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') commitEdit();
        else if (e.key === 'Escape') setEditing(false);
    };

    return (
        <>
            <div className={cn('px-1 py-0.5 font-mono text-muted-foreground truncate', bold && 'font-bold')}>
                {entry.label}
            </div>
            <div
                className={cn(
                    'px-1 py-0.5 text-right tabular-nums font-mono',
                    editable && 'cursor-pointer hover:bg-muted rounded',
                    bold && 'font-bold'
                )}
                onDoubleClick={startEdit}
            >
                {editing ? (
                    <input
                        ref={inputRef}
                        className="w-20 rounded border border-border bg-background px-1 text-right font-mono text-xs outline-none focus:border-primary"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={commitEdit}
                        onKeyDown={handleKeyDown}
                        onClick={(e) => e.stopPropagation()}
                    />
                ) : (
                    <span className={cn(editable && 'border-b border-dashed border-muted-foreground/40')}>
                        {formatValue(entry.value, entry.unit)}
                    </span>
                )}
            </div>
            <div className={cn('px-1 py-0.5 text-muted-foreground', bold && 'font-bold')}>{entry.unit}</div>
        </>
    );
}
